import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import type {
  ContractEditable,
  ContractRenewalStatus,
} from '@/types'

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string }
    return [e.message, e.details, e.hint, e.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

export interface ContractChargeImpact {
  total: number
  confirmed: number
  protectedPeriods: Array<{
    year: number
    month: number
    revenue_checked: boolean
    royalty_checked: boolean
  }>
}

// Editable snapshot of a contract for the shared ContractEditor (RPC get_contract_editable).
export async function loadContractEditable(contractId: number): Promise<ContractEditable | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_contract_editable', { p_contract_id: contractId })
  )
  if (error) throw new Error(formatError(error))
  const rows = (data ?? []) as ContractEditable[]
  return rows[0] ?? null
}

export async function loadContractChargeImpact(contractId: number): Promise<ContractChargeImpact> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_contract_royalty_change_impact', {
      p_contract_id: contractId,
    })
  )
  if (error) throw new Error(formatError(error))
  const row = ((data ?? []) as Array<{
    charges_total?: number | string | null
    confirmed_charges?: number | string | null
    protected_periods?: unknown
  }>)[0]
  const protectedPeriods = Array.isArray(row?.protected_periods)
    ? row.protected_periods.flatMap((period) => {
        if (!period || typeof period !== 'object') return []
        const value = period as Record<string, unknown>
        const year = Number(value.year)
        const month = Number(value.month)
        if (!Number.isInteger(year) || !Number.isInteger(month)) return []
        return [{
          year,
          month,
          revenue_checked: value.revenue_checked === true,
          royalty_checked: value.royalty_checked === true,
        }]
      })
    : []
  return {
    total: Number(row?.charges_total ?? 0),
    confirmed: Number(row?.confirmed_charges ?? 0),
    protectedPeriods,
  }
}

/**
 * Активация договора = отметка «франчайзи подписал». Это операция, а не поле формы:
 * сервер закрывает предыдущий договор серии днём раньше, переносит на новый его
 * условия роялти и проверяет закрытый период, пересечение с живым ДКК и дыру в
 * покрытии. Второй реализации правила не заводить — решает activate_contract.
 */
export async function activateContract(contractId: number, signedAt: string | null): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('activate_contract', {
      p_contract_id: contractId,
      p_signed_at: signedAt || null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export interface ContractPendingTermInput {
  contract_type: 'REGULAR' | 'SUBSCRIPTION'
  sales_model: 'SUBSCRIPTION' | 'MIXED' | null
  effective_from: string
  effective_to: string | null
  royalty_percent: number
  agency_percent: number | null
  vat_percent: number | null
  listok_crm_base_amount: number | null
  is_listok_crm_carrier: boolean
  is_fixed_royalty_carrier: boolean
  min_royalty_amount: number
  currency_code: string
  source_document_note: string | null
  notes: string | null
}

export interface ContractPendingTerm {
  pending_term_id: number
  position: number
  contract_type: 'REGULAR' | 'SUBSCRIPTION'
  // До миграции 20261020190000 RPC колонку не отдаёт.
  sales_model?: 'SUBSCRIPTION' | 'MIXED' | null
  effective_from: string
  effective_to: string | null
  royalty_percent: number
  agency_percent: number | null
  vat_percent: number | null
  listok_crm_base_amount: number | null
  is_listok_crm_carrier: boolean
  is_fixed_royalty_carrier: boolean
  min_royalty_amount: number
  currency_code: string
}

/**
 * «Расторгнуть и подписать новый договор» одним серверным вызовом: заготовка ДС о
 * расторжении, заготовка нового ДКК и его условия. Условия лежат при заготовке и
 * встают в роялти при подписании ДКК (activate_contract) вместо копии старых —
 * поэтому сбой на любом шаге не оставляет полузаведённых документов.
 */
export async function createContractTerminationWithRenewal(input: {
  sourceContractId: number
  terminationDate: string
  terminationNote: string | null
  terminationDocUrl: string | null
  contractNumber: string
  signedAt: string | null
  effectiveFrom: string
  expectedEndDate: string | null
  terms: ContractPendingTermInput[]
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('create_contract_termination_with_renewal', {
      p_source_contract_id: input.sourceContractId,
      p_termination_date: input.terminationDate,
      p_termination_note: input.terminationNote,
      p_termination_doc_url: input.terminationDocUrl,
      p_contract_number: input.contractNumber.trim(),
      p_signed_at: input.signedAt || null,
      p_effective_from: input.effectiveFrom,
      p_expected_end_date: input.expectedEndDate || null,
      p_terms: input.terms,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

/** Условия неподписанного ДКК, которые встанут при отметке подписи. */
export async function loadContractPendingTerms(contractId: number): Promise<ContractPendingTerm[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_contract_pending_terms', { p_contract_id: contractId })
  )
  if (error) throw new Error(formatError(error))
  return ((data ?? []) as any[]).map(row => ({
    ...row,
    royalty_percent: Number(row.royalty_percent),
    agency_percent: row.agency_percent == null ? null : Number(row.agency_percent),
    vat_percent: row.vat_percent == null ? null : Number(row.vat_percent),
    listok_crm_base_amount: row.listok_crm_base_amount == null ? null : Number(row.listok_crm_base_amount),
    min_royalty_amount: Number(row.min_royalty_amount),
  }))
}

export interface ContractFieldsPatch {
  contract_number: string | null
  signed_at: string | null
  effective_from: string | null
  expected_end_date: string | null
  actual_end_date: string | null
  entity_id: number | null
  lump_sum_amount: number | null
  lump_sum_currency: string | null
  levita_entity: string | null
  renewal_status: ContractRenewalStatus | null
}

// Single advanced-gated write path for ALL contract fields (RPC set_contract_fields).
// Full-overwrite semantics: the form sends the whole snapshot.
export async function setContractFields(contractId: number, patch: ContractFieldsPatch): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_contract_fields', {
      p_contract_id: contractId,
      p_contract_number: patch.contract_number,
      p_signed_at: patch.signed_at,
      p_effective_from: patch.effective_from,
      p_expected_end_date: patch.expected_end_date,
      p_actual_end_date: patch.actual_end_date,
      p_entity_id: patch.entity_id,
      p_lump_sum_amount: patch.lump_sum_amount,
      p_lump_sum_currency: patch.lump_sum_currency,
      p_levita_entity: patch.levita_entity,
      p_renewal_status: patch.renewal_status,
    })
  )
  if (error) throw new Error(formatError(error))
}
